import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { apiFetch, createStreamFetch } from '../api';
import { useDemoMode } from '../context/DemoModeContext';
import {
  StudentRosterTable,
  StudentTableFilters,
  StudentDetailPanel
} from '../components/roster';
import TutorMatchPanel from '../components/roster/TutorMatchPanel';
import ApprovalPanel from '../components/ApprovalPanel';
import AddStudentPanel from '../components/AddStudentPanel';
import AddTutorPanel from '../components/AddTutorPanel';
import HelperIcon from '../components/HelperIcon';
import { helperContent } from '../content/helper-content';
import useTableSelection from '../hooks/useTableSelection';
import useStudentRoster from '../hooks/useStudentRoster';
import { useWorkflow } from '../context/WorkflowContext';
import { ROSTER_SELECTED_TUTOR_SCOPE_KEY } from '../constants/rosterScope';
import { getStudentDisplayName } from '../utils/studentName';
import toast from '../utils/toast';
import '../styles/student-roster.css';

const StudentRoster = () => {
  // Role check
  const { getEffectiveRole } = useDemoMode();
  const userRole = getEffectiveRole();
  const isAdmin = userRole === 'admin';
  const isTeamCaptain = userRole === 'team_captain';

  // Tutor list and selection
  const [tutors, setTutors] = useState([]);
  // Admin starts with 'all', team captain needs to fetch their tutor_id first
  const [selectedTutorId, setSelectedTutorId] = useState(isAdmin ? 'all' : null);
  const [tutorsLoading, setTutorsLoading] = useState(true);
  const [userTutorId, setUserTutorId] = useState(null);

  // Use custom hooks for roster data and selection
  const {
    students,
    tutorInfo,
    loading: rosterLoading,
    error: rosterError,
    filters,
    updateFilters,
    resetFilters,
    sortConfig,
    updateSort,
    setSort,
    availableSubjects,
    unassignedCount,
    isAllMode,
    refetch
  } = useStudentRoster(selectedTutorId);

  // Table selection
  const studentIds = useMemo(() => students.map(s => s.uuid), [students]);
  const {
    selectedIds,
    selectedCount,
    toggleSelection,
    toggleAll,
    clearSelection,
    getSelectionState
  } = useTableSelection();

  const { isAllSelected, isPartiallySelected } = getSelectionState(studentIds);

  // Workflow context - selecting a student triggers the WorkflowGuide
  const { selectStudent } = useWorkflow();

  // Pending matches (admin only)
  const [pendingMatches, setPendingMatches] = useState([]);
  const [showApprovalPanel, setShowApprovalPanel] = useState(false);

  // Add student panel
  const [showAddStudentPanel, setShowAddStudentPanel] = useState(false);

  // Student detail/edit panel
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [archivedStudents, setArchivedStudents] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [reinstatingStudentUuid, setReinstatingStudentUuid] = useState(null);
  const [isArchivedExpanded, setIsArchivedExpanded] = useState(false);

  // Add tutor panel
  const [showAddTutorPanel, setShowAddTutorPanel] = useState(false);

  // Tutor matchmaking state
  const [matchingState, setMatchingState] = useState({
    isOpen: false,
    student: null,
    isStreaming: false,
    progress: { message: '' },
    matches: [],
    error: null,
    isAssigning: false,
    assigningTutorId: null
  });
  const abortControllerRef = useRef(null);

  // Fetch team captain's profile_id on mount and auto-select their roster
  useEffect(() => {
    if (isTeamCaptain) {
      const fetchUserProfileId = async () => {
        try {
          const response = await apiFetch('/api/auth/me');
          if (response.ok) {
            const data = await response.json();
            if (data.profile_id) {
              setUserTutorId(data.profile_id);
              setSelectedTutorId(data.profile_id);
            }
          }
        } catch (err) {
          console.error('Error fetching user profile_id:', err);
        }
      };
      fetchUserProfileId();
    }
  }, [isTeamCaptain]);

  // Clear selection when tutor changes
  useEffect(() => {
    clearSelection();
  }, [selectedTutorId, clearSelection]);

  // Persist selected tutor scope so other pages (Sessions) can mirror My Students filter.
  useEffect(() => {
    if (selectedTutorId === null || selectedTutorId === undefined || selectedTutorId === '') {
      return;
    }
    const serializedScope = String(selectedTutorId);
    try {
      localStorage.setItem(ROSTER_SELECTED_TUTOR_SCOPE_KEY, serializedScope);
    } catch {}
    try {
      sessionStorage.setItem(ROSTER_SELECTED_TUTOR_SCOPE_KEY, serializedScope);
    } catch {}
  }, [selectedTutorId]);

  const fetchTutors = async () => {
    setTutorsLoading(true);
    try {
      const response = await apiFetch('/api/tutors');
      if (!response.ok) {
        throw new Error('Failed to fetch tutors');
      }
      const data = await response.json();
      setTutors(data.tutors || []);
    } catch (err) {
      console.error('Error fetching tutors:', err);
    } finally {
      setTutorsLoading(false);
    }
  };

  const fetchPendingMatches = async () => {
    try {
      const response = await apiFetch('/api/admin/pending-matches');
      if (!response.ok) {
        throw new Error('Failed to fetch pending matches');
      }
      const data = await response.json();
      setPendingMatches(data.pending_matches || []);
    } catch (err) {
      console.error('Error fetching pending matches:', err);
    }
  };

  const handleApprove = async (matchId) => {
    try {
      const response = await apiFetch(`/api/demo/matches/${matchId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'ACTIVE' })
      });

      if (!response.ok) {
        throw new Error('Failed to approve match');
      }

      setPendingMatches(prev => prev.filter(m => m.match_id !== matchId));

      if (selectedTutorId) {
        refetch();
      }
    } catch (err) {
      console.error('Error approving match:', err);
      alert('Failed to approve match. Please try again.');
    }
  };

  const handleReject = async (matchId) => {
    try {
      const response = await apiFetch(`/api/demo/matches/${matchId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'CANCELLED' })
      });

      if (!response.ok) {
        throw new Error('Failed to reject match');
      }

      setPendingMatches(prev => prev.filter(m => m.match_id !== matchId));
    } catch (err) {
      console.error('Error rejecting match:', err);
      alert('Failed to reject match. Please try again.');
    }
  };

  const handleTutorChange = (e) => {
    const value = e.target.value;
    // Handle special 'all' value for admin view
    if (value === 'all') {
      setSelectedTutorId('all');
    } else {
      setSelectedTutorId(value ? parseInt(value, 10) : null);
    }
  };

  const handleStudentCreated = (newStudent) => {
    setShowAddStudentPanel(false);
    const name = [newStudent.first_name, newStudent.last_name].filter(Boolean).join(' ')
      || newStudent.display_name
      || 'New Student';
    refetch();
    toast.success(`Student "${name}" created successfully.`);
  };

  const fetchArchivedStudents = useCallback(async () => {
    if (!isAdmin) return;
    setArchivedLoading(true);
    try {
      const response = await apiFetch('/api/admin/archived-students');
      if (!response.ok) {
        throw new Error('Failed to fetch archived students');
      }
      const data = await response.json();
      setArchivedStudents(data.students || []);
    } catch (err) {
      console.error('Error fetching archived students:', err);
    } finally {
      setArchivedLoading(false);
    }
  }, [isAdmin]);

  // Fetch tutors list on mount
  useEffect(() => {
    fetchTutors();
    if (isAdmin) {
      fetchPendingMatches();
      fetchArchivedStudents();
    }
  }, [isAdmin, fetchArchivedStudents]);

  const handleTutorCreated = (newTutor) => {
    setShowAddTutorPanel(false);
    fetchTutors();
    toast.success(`Tutor "${newTutor.first_name} ${newTutor.last_name}" created successfully.`);
  };

  // Select student in workflow - this triggers the WorkflowGuide to expand
  const handleViewDetails = (student) => {
    const displayName = getStudentDisplayName(student);

    selectStudent({
      uuid: student.uuid,
      name: displayName,
      grade_year_level: student.grade_year_level,
      target_subjects: student.target_subjects,
      learning_style: student.learning_style,
      session_count: student.session_count,
      confidence_score: student.confidence_score,
      assigned_at: student.assigned_at,
      goals: student.goals
    });
    setSelectedStudent(student);
  };

  const handleCloseDetails = () => {
    setSelectedStudent(null);
  };

  const handleStudentUpdated = (updatedStudent) => {
    setSelectedStudent(prev => ({ ...prev, ...updatedStudent }));
    refetch();
    toast.success('Student updated successfully.');
  };

  const handleStudentDeleted = (deletedStudent) => {
    setSelectedStudent(null);
    refetch();
    if (isAdmin) {
      fetchArchivedStudents();
    }
    const name = [deletedStudent.first_name, deletedStudent.last_name].filter(Boolean).join(' ')
      || deletedStudent.display_name
      || 'Student';
    toast.success(`"${name}" archived successfully.`);
  };

  const handleReinstateStudent = async (student) => {
    setReinstatingStudentUuid(student.uuid);
    try {
      const response = await apiFetch(`/api/students/${student.uuid}/reinstate`, {
        method: 'PUT'
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to reinstate student');
      }

      await fetchArchivedStudents();
      refetch();
      const name = [student.first_name, student.last_name].filter(Boolean).join(' ') || 'Student';
      toast.success(`"${name}" reinstated successfully.`);
    } catch (err) {
      console.error('Error reinstating student:', err);
      toast.error(err.message || 'Failed to reinstate student.');
    } finally {
      setReinstatingStudentUuid(null);
    }
  };

  const handleBulkAction = (action) => {
    if (action === 'export') {
      // Export selected students as CSV
      const selectedStudents = students.filter(s => selectedIds.has(s.uuid));
      const csv = [
        ['ID', 'Grade', 'Subjects', 'Sessions', 'Assigned Date'].join(','),
        ...selectedStudents.map(s => [
          s.uuid,
          s.grade_year_level || '',
          `"${s.target_subjects || ''}"`,
          s.session_count || 0,
          s.assigned_at || ''
        ].join(','))
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `students-export-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleSortChange = (config) => {
    setSort(config);
  };

  // Handle tutor assignment from dropdown (admin only)
  const handleTutorAssignment = (studentUuid, tutorId, tutorName) => {
    // Refetch the roster to get updated data
    refetch();
  };

  // Handle starting the matchmaking process for a student
  const handleStartMatch = useCallback(async (student) => {
    // Cancel any existing stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    // Open panel and start streaming
    setMatchingState({
      isOpen: true,
      student,
      isStreaming: true,
      progress: { message: 'Analyzing student profile...' },
      matches: [],
      error: null,
      isAssigning: false,
      assigningTutorId: null
    });

    try {
      const response = await createStreamFetch('/api/demo/match-student-stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_uuid: student.uuid }),
        signal: abortControllerRef.current.signal
      });

      // Handle rate limit
      if (response.status === 429) {
        setMatchingState(prev => ({
          ...prev,
          isStreaming: false,
          error: 'Rate limit reached. Please wait and try again.'
        }));
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to find tutor matches');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          setMatchingState(prev => ({ ...prev, isStreaming: false }));
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));

              switch (event.type) {
                case 'thinking_start':
                  setMatchingState(prev => ({
                    ...prev,
                    progress: { message: event.data.message || 'Analyzing...' }
                  }));
                  break;

                case 'thinking_end':
                  setMatchingState(prev => ({
                    ...prev,
                    progress: { message: event.data.message || 'Finding best matches...' }
                  }));
                  break;

                case 'matches_complete':
                  setMatchingState(prev => ({
                    ...prev,
                    isStreaming: false,
                    matches: event.data.matches || []
                  }));
                  // Track cost if provided
                  if (event.data?.cost_data) {
                    const cost = event.data.cost_data.cost || event.data.cost_data.total_cost;
                    if (typeof cost === 'number' && cost > 0) {
                      window.dispatchEvent(new CustomEvent('lrbrain-cost-update', { detail: { cost } }));
                    }
                  }
                  break;

                case 'error':
                  if (event.data.status === 429 || event.data.code === 'RATE_LIMITED' ||
                      (event.data.error && event.data.error.toLowerCase().includes('rate limit'))) {
                    toast.rateLimit(event.data.retry_after || null);
                    setMatchingState(prev => ({
                      ...prev,
                      isStreaming: false,
                      error: 'Rate limit reached. Please wait and try again.'
                    }));
                  } else {
                    setMatchingState(prev => ({
                      ...prev,
                      isStreaming: false,
                      error: event.data.error || 'An error occurred'
                    }));
                  }
                  break;

                case 'done':
                  setMatchingState(prev => ({ ...prev, isStreaming: false }));
                  break;

                default:
                  break;
              }
            } catch (e) {
              console.error('Error parsing SSE event:', e);
            }
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        // User closed panel, ignore
        return;
      }
      console.error('Matchmaking error:', err);
      setMatchingState(prev => ({
        ...prev,
        isStreaming: false,
        error: err.message || 'Failed to find tutor matches'
      }));
    }
  }, []);

  // Handle assigning a tutor from the match panel
  const handleAssignTutor = useCallback(async (tutorId) => {
    if (!matchingState.student) return;

    setMatchingState(prev => ({
      ...prev,
      isAssigning: true,
      assigningTutorId: tutorId
    }));

    try {
      const response = await apiFetch(`/api/students/${matchingState.student.uuid}/assign`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tutor_id: tutorId })
      });

      if (!response.ok) {
        throw new Error('Failed to assign tutor');
      }

      // Success - close panel and refresh roster
      setMatchingState({
        isOpen: false,
        student: null,
        isStreaming: false,
        progress: { message: '' },
        matches: [],
        error: null,
        isAssigning: false,
        assigningTutorId: null
      });

      // Refresh the roster to show updated assignment
      refetch();

      toast.success('Tutor assigned successfully!');
    } catch (err) {
      console.error('Assignment error:', err);
      setMatchingState(prev => ({
        ...prev,
        isAssigning: false,
        assigningTutorId: null
      }));
      toast.error('Failed to assign tutor. Please try again.');
    }
  }, [matchingState.student, refetch]);

  // Handle closing the match panel
  const handleCloseMatchPanel = useCallback(() => {
    // Abort any in-progress stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setMatchingState({
      isOpen: false,
      student: null,
      isStreaming: false,
      progress: { message: '' },
      matches: [],
      error: null,
      isAssigning: false,
      assigningTutorId: null
    });
  }, []);

  return (
    <div className="student-roster student-roster--table-view">
      <div className="roster-table-container">
        {/* Header */}
        <header className="roster-table-header">
          <div className="roster-table-header__left">
            <h1>Students</h1>
            <HelperIcon
              title={helperContent.studentRoster.title}
              content={helperContent.studentRoster.content}
              size="medium"
            />
          </div>

          <div className="roster-table-header__right">
            {/* Tutor Selector - Admin only (team captains auto-filter to their students) */}
            {isAdmin && (
              <div className="roster-table-header__tutor-select">
                <select
                  id="tutor-select"
                  value={selectedTutorId || ''}
                  onChange={handleTutorChange}
                  disabled={tutorsLoading}
                >
                  <option value="">
                    {tutorsLoading ? 'Loading...' : 'Select Tutor'}
                  </option>
                  {/* Admin-only: All Students option */}
                  <option value="all">
                    All Students
                  </option>
                  {tutors.map(tutor => (
                    <option key={tutor.id} value={tutor.id}>
                      {tutor.name}
                    </option>
                  ))}
                </select>
                {/* Unassigned count badge when in All mode */}
                {isAllMode && unassignedCount > 0 && (
                  <span className="roster-table-header__unassigned-badge">
                    {unassignedCount} unassigned
                  </span>
                )}
              </div>
            )}

            {/* Admin Actions */}
            {isAdmin && pendingMatches.length > 0 && (
              <button
                className="roster-table-header__btn roster-table-header__btn--pending"
                onClick={() => setShowApprovalPanel(true)}
              >
                Review Pending
                <span className="roster-table-header__badge">{pendingMatches.length}</span>
              </button>
            )}

            {/* Add Student - Admin and Team Captain */}
            {(isAdmin || isTeamCaptain) && (
              <button
                className="roster-table-header__btn roster-table-header__btn--add"
                onClick={() => setShowAddStudentPanel(true)}
              >
                + Add Student
              </button>
            )}

            {isAdmin && (
              <button
                className="roster-table-header__btn roster-table-header__btn--add-tutor"
                onClick={() => setShowAddTutorPanel(true)}
              >
                + Add Tutor
              </button>
            )}
          </div>
        </header>

        {/* Main Content */}
        <div className="roster-table-content">
          {!selectedTutorId && (
            <div className="roster-table-welcome">
              <div className="roster-table-welcome__icon">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                  <circle cx="32" cy="24" r="12" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 52C12 41.5066 20.5066 33 31 33H33C43.4934 33 52 41.5066 52 52" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <>
                <h2>Welcome to the Student Roster</h2>
                <p>Select a tutor from the dropdown above to view their assigned students.</p>
              </>
            </div>
          )}

          {selectedTutorId && rosterLoading && (
            <div className="roster-table-loading">
              <div className="roster-table-loading__spinner"></div>
              <p>Loading students...</p>
            </div>
          )}

          {selectedTutorId && rosterError && !rosterLoading && (
            <div className="roster-table-error">
              <p>Error: {rosterError}</p>
              <button onClick={refetch}>Try Again</button>
            </div>
          )}

          {selectedTutorId && !rosterLoading && !rosterError && (
            <>
              {/* Filters */}
              <StudentTableFilters
                filters={filters}
                onFiltersChange={updateFilters}
                onReset={resetFilters}
                sortConfig={sortConfig}
                onSortChange={handleSortChange}
                availableSubjects={availableSubjects}
                selectedCount={selectedCount}
                onBulkAction={handleBulkAction}
              />

              {/* Table */}
              <StudentRosterTable
                students={students}
                tutorInfo={tutorInfo}
                sortConfig={sortConfig}
                onSort={updateSort}
                selectedIds={selectedIds}
                onToggleSelection={toggleSelection}
                onToggleAll={() => toggleAll(studentIds)}
                isAllSelected={isAllSelected}
                isPartiallySelected={isPartiallySelected}
                onViewDetails={handleViewDetails}
                isAllMode={isAllMode}
                isAdmin={isAdmin}
                tutors={tutors}
                onTutorAssignment={handleTutorAssignment}
                onStartMatch={handleStartMatch}
                isMatchingActive={matchingState.isOpen}
                matchingStudentUuid={matchingState.student?.uuid}
              />

              {isAdmin && (
                <section className={`archived-students-section ${isArchivedExpanded ? 'archived-students-section--expanded' : 'archived-students-section--collapsed'}`}>
                  <div className="archived-students-section__header">
                    <div className="archived-students-section__title-wrap">
                      <h3>Archived Students</h3>
                      <span className="archived-students-section__count">{archivedStudents.length}</span>
                    </div>
                    <div className="archived-students-section__header-actions">
                      {archivedLoading && <span className="archived-students-section__loading">Loading...</span>}
                      <button
                        type="button"
                        className="archived-students-section__toggle-btn"
                        onClick={() => setIsArchivedExpanded(prev => !prev)}
                        aria-expanded={isArchivedExpanded}
                      >
                        <span>{isArchivedExpanded ? 'Collapse' : 'Expand'}</span>
                        <svg
                          className={`archived-students-section__chevron ${isArchivedExpanded ? 'archived-students-section__chevron--up' : 'archived-students-section__chevron--down'}`}
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                        >
                          <path
                            d="M3 5.5L7 9L11 5.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {isArchivedExpanded && (
                    <>
                      {!archivedLoading && archivedStudents.length === 0 && (
                        <p className="archived-students-section__empty">No archived students.</p>
                      )}
                      {archivedStudents.length > 0 && (
                        <div className="archived-students-section__list">
                          {archivedStudents.map(student => (
                            <div key={student.uuid} className="archived-students-section__item">
                              <div className="archived-students-section__meta">
                                <strong>
                                  {[student.first_name, student.last_name].filter(Boolean).join(' ') || `Student ${student.uuid.substring(0, 8)}`}
                                </strong>
                                <span>{student.grade_year_level || 'Grade not set'}</span>
                                <span>{student.target_subjects || 'No subjects'}</span>
                              </div>
                              <button
                                className="archived-students-section__reinstate-btn"
                                onClick={() => handleReinstateStudent(student)}
                                disabled={reinstatingStudentUuid === student.uuid}
                              >
                                {reinstatingStudentUuid === student.uuid ? 'Reinstating...' : 'Reinstate'}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {!isArchivedExpanded && (
                    <div className="archived-students-section__collapsed-summary">
                      {archivedStudents.length > 0 ? (
                        <span>{archivedStudents.length} archived student{archivedStudents.length !== 1 ? 's' : ''} available to restore.</span>
                      ) : (
                        <span>No archived students.</span>
                      )}
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </div>

      {/* Admin Panels */}
      {isAdmin && showApprovalPanel && (
        <ApprovalPanel
          matches={pendingMatches}
          onApprove={handleApprove}
          onReject={handleReject}
          onClose={() => setShowApprovalPanel(false)}
        />
      )}

      {(isAdmin || isTeamCaptain) && showAddStudentPanel && (
        <AddStudentPanel
          onClose={() => setShowAddStudentPanel(false)}
          onStudentCreated={handleStudentCreated}
          isAdmin={isAdmin}
          assignToTutorId={isTeamCaptain ? userTutorId : null}
        />
      )}

      {isAdmin && showAddTutorPanel && (
        <AddTutorPanel
          onClose={() => setShowAddTutorPanel(false)}
          onTutorCreated={handleTutorCreated}
        />
      )}

      {/* Tutor Matchmaking Panel */}
      {isAdmin && matchingState.isOpen && (
        <TutorMatchPanel
          student={matchingState.student}
          matches={matchingState.matches}
          isStreaming={matchingState.isStreaming}
          progress={matchingState.progress}
          error={matchingState.error}
          onAssign={handleAssignTutor}
          onClose={handleCloseMatchPanel}
          isAssigning={matchingState.isAssigning}
          assigningTutorId={matchingState.assigningTutorId}
        />
      )}

      {selectedStudent && (
        <StudentDetailPanel
          student={selectedStudent}
          tutorInfo={tutorInfo}
          onClose={handleCloseDetails}
          onStudentUpdated={handleStudentUpdated}
          onStudentDeleted={handleStudentDeleted}
          canEdit={isAdmin || isTeamCaptain}
        />
      )}
    </div>
  );
};

export default StudentRoster;
